import React, {useMemo} from 'react';
import {Divider} from 'antd';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {t} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {dbTimeFormat} from '../../../../../../services/Helpers';
import VStack from '../../../../../common/Layout/VStack';
import HStack from '../../../../../common/Layout/HStack';
import Box from '../../../../../common/Layout/Box';
import AppText from '../../../../../common/AppText';
import UserAvatar from '../../../../../common/UserAvatar';

const cardStyle = {
    backgroundColor: Colors.newDesign.white,
    border: `1px solid ${Colors.slotBorder}`,
    borderRadius: 4,
    overflow: 'hidden',
};

const formatSwapDayTimeLabel = (day, boxLocale, timeFormat) => {
    if (!day) {
        return '';
    }
    const weekday = day.day_of_week != null
        ? moment().weekday(day.day_of_week).locale(boxLocale).format('dddd')
        : day.day_name;
    const start = moment(day.start, dbTimeFormat).locale(boxLocale).format(timeFormat);
    const end = moment(day.end, dbTimeFormat).locale(boxLocale).format(timeFormat);
    return `${weekday}: ${start} - ${end}`;
};

const SwapDaySummary = ({target, values}) => {
    const user = target?.user;
    const swapDayData = values?.swapDayData;
    const lang = useSelector(state => state.userSettings.lang);
    const box = useSelector(state => state.box.box);

    const boxLocale = useMemo(() => (lang?.language === 'Hebrew' ? 'he' : 'en'), [lang]);
    const timeFormat = useMemo(() => (box ? box.locations_box[0].time_format : null), [box]);

    const fromDay = useMemo(
        () => swapDayData?.current_days?.find(day => day.id === values.fromSeriesEventId),
        [swapDayData, values.fromSeriesEventId],
    );

    const toDay = useMemo(
        () => swapDayData?.all_days?.find(day => day.id === values.toSeriesEventId),
        [swapDayData, values.toSeriesEventId],
    );

    const dateRange = useMemo(() => {
        const startDateRaw = swapDayData?.start_date ?? swapDayData?.series_start_date;
        if (!startDateRaw) {
            return null;
        }
        const formatDate = (date) => moment(date).locale(boxLocale).format(boxLocale === 'he' ? 'D MMM, YYYY' : 'MMM D, YYYY');
        const startDate = formatDate(startDateRaw);
        const endDateRaw = swapDayData?.end_date ?? swapDayData?.series_end_date;
        if (!endDateRaw) {
            return t('no-end-date-hugim', {startDate});
        }
        return `${startDate} - ${formatDate(endDateRaw)}`;
    }, [swapDayData, boxLocale]);

    const fromDayLabel = formatSwapDayTimeLabel(fromDay, boxLocale, timeFormat);
    const toDayLabel = formatSwapDayTimeLabel(toDay, boxLocale, timeFormat);

    return (
        <VStack space={'0'} w={'100%'}>
            <Box style={{height: 1, backgroundColor: Colors.slotBorder}}/>
            <VStack space={'0.75rem'} style={{paddingTop: '27px'}}>
                <AppText boldFont fontSize={GlobalStyleAttributes.fonts.heading2Desktop} color={Colors.newDesign.mainText}>
                    {t('bookingDetails')}
                </AppText>
                <div style={cardStyle}>
                    <div style={{padding: '12px'}}>
                        <VStack space={'0.75rem'}>
                            {user && (
                                <HStack space={'0.75rem'} alignItems={'center'}>
                                    <UserAvatar
                                        imageUri={user.image}
                                        firstName={user.firstName}
                                        lastName={user.lastName}
                                        size={31}
                                    />
                                    <AppText mediumFont style={{lineHeight: '24px'}}>
                                        {user.firstName} {user.lastName}
                                    </AppText>
                                </HStack>
                            )}
                            <VStack space={'0.75rem'}>
                                <HStack space={'0.25rem'} alignItems={'center'} flexWrap={'wrap'}>
                                    <AppText mediumFont>{`${t('courseLimitationsCourseName')}:`}</AppText>
                                    <AppText>{swapDayData?.series_name ?? ''}</AppText>
                                </HStack>
                                {dateRange && (
                                    <HStack space={'0.25rem'} alignItems={'center'} flexWrap={'wrap'}>
                                        <AppText mediumFont>{`${t('date-of-hug-label')}:`}</AppText>
                                        <AppText>{dateRange}</AppText>
                                    </HStack>
                                )}
                            </VStack>
                        </VStack>
                    </div>
                    <Divider style={{margin: 0, borderColor: Colors.slotBorder}}/>
                    <div style={{padding: '12px'}}>
                        <VStack space={'0.75rem'}>
                            <VStack space={'0'}>
                                <AppText mediumFont>{`${t('swap-day-replace-label')}:`}</AppText>
                                <AppText>{fromDayLabel}</AppText>
                            </VStack>
                            <VStack space={'0'}>
                                <AppText mediumFont>{`${t('swap-day-to-label')}:`}</AppText>
                                <AppText>{toDayLabel}</AppText>
                            </VStack>
                        </VStack>
                    </div>
                </div>
            </VStack>
        </VStack>
    );
};

export default SwapDaySummary;
